import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Loader2, ArrowLeft, Package, XCircle, Wallet,
  Send, ShieldCheck, X, ChevronDown, ChevronRight, DollarSign,
  AlertTriangle, Printer, FileDown, FileText,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { isKRSuperAdmin } from '@/lib/utils';
import { BATCH_STATUS_META, LINE_STATUS_META, money, fmtDate, TOASTS } from '@/lib/accountingConstants';
import PaymentMethodEditor from '@/components/accounting/PaymentMethodEditor';
import MarkFailedDialog from '@/components/accounting/MarkFailedDialog';

export default function PaymentBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRSuperAdmin(user);

  const [batch, setBatch] = useState(null);
  const [lines, setLines] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [expanded, setExpanded] = useState({}); // batch_line_id → bool
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [failLine, setFailLine] = useState(null); // batch_line for failure dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, l, s] = await Promise.all([
        accountingAPI.getBatch(batchId),
        accountingAPI.getBatchLines(batchId),
        accountingAPI.getBatchSources(batchId).catch(() => ({ data: { items: [] } })),
      ]);
      setBatch(b.data);
      setLines(l.data.items || []);
      setSources(s.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load batch');
    } finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const meta = batch ? (BATCH_STATUS_META[batch.status] || BATCH_STATUS_META.draft) : BATCH_STATUS_META.draft;
  const StatusIcon = meta.icon;

  const canSubmit = batch?.status === 'draft';
  const canApprove = batch?.status === 'pending_approval' && isKR;
  const canMarkReady = batch?.status === 'approved' && isKR;
  const canReject = batch?.status === 'pending_approval' && isKR;
  const canCancel = ['draft', 'pending_approval'].includes(batch?.status);
  const canMarkPaid = batch?.status === 'ready_for_payment' && isKR;
  const canFail = ['ready_for_payment', 'paid', 'completed_with_exceptions'].includes(batch?.status) && isKR;
  const readyForMethodEdit = batch?.status === 'ready_for_payment';

  // Do every payable line have method + required fields confirmed?
  const allLinesReady = lines.every((ln) => {
    if (ln.line_type === 'zero_net') return true;
    if (!ln.method_confirmed_at && !(ln.actual_payment_method && (ln.ach_payload || ln.check_payload))) return false;
    const m = (ln.actual_payment_method || '').toLowerCase();
    if (m === 'ach') {
      return !!(ln.ach_payload?.payment_date && ln.ach_payload?.ach_reference);
    }
    if (m === 'check') {
      return !!(ln.check_payload?.check_number && ln.check_payload?.check_date && ln.check_payload?.delivery_method);
    }
    return false;
  });

  const runAction = async (fn, successMsg) => {
    setActioning(true);
    try {
      const { data } = await fn();
      setBatch(data);
      toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || TOASTS.ACTION_FAILED);
    } finally { setActioning(false); }
  };

  const doSubmit = () => runAction(() => accountingAPI.submitBatch(batchId), TOASTS.SUBMITTED);
  const doApprove = () => runAction(() => accountingAPI.approveBatch(batchId), TOASTS.APPROVED);
  const doMarkReady = () => runAction(() => accountingAPI.markBatchReady(batchId), TOASTS.BATCH_MARKED_READY);
  const doMarkPaid = async () => {
    setActioning(true);
    try {
      const { data } = await accountingAPI.markBatchPaid(batchId);
      setBatch(data);
      // Reload lines to reflect execution_status
      const l = await accountingAPI.getBatchLines(batchId);
      setLines(l.data.items || []);
      toast.success(TOASTS.BATCH_PAID);
    } catch (e) {
      toast.error(e.response?.data?.detail || TOASTS.ACTION_FAILED);
    } finally { setActioning(false); }
  };

  const handleMethodUpdated = (updatedLine) => {
    setLines((prev) => prev.map((ln) => (ln.batch_line_id === updatedLine.batch_line_id ? { ...ln, ...updatedLine } : ln)));
  };

  const handleFailed = async () => {
    await load();
    toast.success(TOASTS.LINE_FAILED);
  };

  const exportPdf = async (variant, action = 'download') => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.batchExportUrl(batchId, variant)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        // Try to surface the backend's error message so the user can
        // report it (or self-diagnose) instead of a generic failure.
        let detail = '';
        try {
          const err = await r.json();
          detail = err?.detail || err?.message || '';
        } catch { /* not JSON */ }
        throw new Error(detail || `PDF export failed (HTTP ${r.status})`);
      }
      const b = await r.blob();
      const objUrl = URL.createObjectURL(b);
      if (action === 'print') {
        const w = window.open(objUrl, '_blank');
        if (w) w.addEventListener('load', () => w.print());
      } else {
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `${batch.batch_number}-${variant}.pdf`;
        a.click();
        URL.revokeObjectURL(objUrl);
      }
    } catch (err) {
      toast.error(err?.message || 'PDF export failed');
    }
  };

  const doReject = async () => {
    if (rejectReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    await runAction(() => accountingAPI.rejectBatch(batchId, { reason: rejectReason.trim() }), TOASTS.REJECTED);
    setRejectOpen(false);
    setRejectReason('');
  };

  const doCancel = async () => {
    await runAction(() => accountingAPI.cancelBatch(batchId, { reason: cancelReason.trim() || undefined }), TOASTS.CANCELLED);
    setCancelOpen(false);
    setCancelReason('');
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!batch) return null;

  return (
    <div className="p-6 space-y-6" data-testid="batch-detail-page">
      <PageHeader
        title={<span className="font-mono">{batch.batch_number}</span>}
        description={batch.notes || 'Payment batch detail — status, approval flow, and included statements.'}
        breadcrumbs={[
          { label: 'Accounting', to: '/accounting' },
          { label: 'Payment Batches', to: '/accounting/payment-batches' },
          { label: batch.batch_number },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate('/accounting/payment-batches')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        }
      />

      {/* Status band + summary */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Badge className={`${meta.tone} border text-sm px-3 py-1`} data-testid="batch-status">
              <StatusIcon className="h-4 w-4 mr-1.5 inline" /> {meta.label}
            </Badge>
            <div className="text-xs text-slate-500">
              Created {fmtDate(batch.created_at)} by <b>{batch.created_by_name}</b>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSubmit && (
              <Button size="sm" onClick={doSubmit} disabled={actioning} data-testid="submit-btn">
                <Send className="h-4 w-4 mr-1.5" /> Submit for Approval
              </Button>
            )}
            {canApprove && (
              <Button size="sm" onClick={doApprove} disabled={actioning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Approve
              </Button>
            )}
            {canMarkReady && (
              <Button size="sm" onClick={doMarkReady} disabled={actioning} className="bg-blue-600 hover:bg-blue-700" data-testid="mark-ready-btn">
                <Wallet className="h-4 w-4 mr-1.5" /> Mark Ready for Payment
              </Button>
            )}
            {canMarkPaid && (
              <Button
                size="sm"
                onClick={doMarkPaid}
                disabled={actioning || !allLinesReady}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="mark-paid-btn"
                title={!allLinesReady ? 'Confirm payment method on every recipient first' : ''}
              >
                <DollarSign className="h-4 w-4 mr-1.5" /> Mark Batch Paid
              </Button>
            )}
            {canReject && (
              <Button size="sm" variant="destructive" onClick={() => setRejectOpen(true)} disabled={actioning} data-testid="reject-btn">
                <XCircle className="h-4 w-4 mr-1.5" /> Reject
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="ghost" onClick={() => setCancelOpen(true)} disabled={actioning} data-testid="cancel-btn">
                <X className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
            )}
            {/* Export / Print — always available once past draft */}
            {batch.status !== 'draft' && (
              <>
                <Button size="sm" variant="outline" onClick={() => exportPdf('summary', 'download')} data-testid="export-summary-btn">
                  <FileDown className="h-4 w-4 mr-1.5" /> Summary PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportPdf('full', 'download')} data-testid="export-full-btn">
                  <FileDown className="h-4 w-4 mr-1.5" /> Full PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportPdf('full', 'print')} data-testid="print-btn">
                  <Printer className="h-4 w-4 mr-1.5" /> Print
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sources — reverse navigation to the Carrier Import(s) that
          produced the statements in this batch (Phase 27.30.5). */}
      {sources.length > 0 && (
        <Card data-testid="batch-sources-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 mb-2">
              <FileText className="h-3.5 w-3.5" />
              Source Imports
            </div>
            <div className="flex flex-wrap gap-2">
              {sources.map((src) => (
                <button
                  key={src.import_id}
                  type="button"
                  onClick={() => navigate(`/accounting/carrier-imports/${src.import_id}`)}
                  className="group flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-sky-400 hover:bg-sky-50 hover:text-sky-800 transition"
                  data-testid={`batch-source-${src.import_id}`}
                  title={src.file_original_filename || ''}
                >
                  <span className="font-medium">{src.carrier_name || 'Carrier Import'}</span>
                  {src.period_label && (
                    <span className="text-xs text-slate-500 group-hover:text-sky-700">· {src.period_label}</span>
                  )}
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {src.statement_count} stmt{src.statement_count === 1 ? '' : 's'}
                  </Badge>
                  <ArrowLeft className="h-3.5 w-3.5 rotate-180 opacity-40 group-hover:opacity-100" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Method-confirmation warning */}
      {canMarkPaid && !allLinesReady && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>Confirm the payment method + required fields on every payable recipient before executing the batch.</div>
        </div>
      )}

      {/* Financial summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Recipients" value={batch.recipient_count} icon={Package} />
        <SummaryCard label="Positive Total" value={money(batch.total_positive)} tone="emerald" />
        <SummaryCard label="Recovery Offset" value={batch.total_offset > 0 ? `-${money(batch.total_offset)}` : money(0)} tone="orange" />
        <SummaryCard label="Credits" value={batch.total_manual_credit > 0 ? `+${money(batch.total_manual_credit)}` : money(0)} tone="violet" />
        <SummaryCard label="Net Payable" value={money(batch.total_net_payable)} tone="slate" bold />
      </div>

      {/* Lines */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Included Recipients ({lines.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="w-8"></th>
                  <th className="text-left px-3 py-2 font-medium">Recipient</th>
                  <th className="text-left px-3 py-2 font-medium">Method</th>
                  <th className="text-right px-3 py-2 font-medium">Positive</th>
                  <th className="text-right px-3 py-2 font-medium">Offset</th>
                  <th className="text-right px-3 py-2 font-medium">Credit</th>
                  <th className="text-right px-3 py-2 font-medium">Net</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Statements</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln) => {
                  const isOpen = !!expanded[ln.batch_line_id];
                  const execStatus = ln.execution_status || 'pending';
                  const lineMeta = LINE_STATUS_META[execStatus] || LINE_STATUS_META.pending;
                  return (
                    <React.Fragment key={ln.batch_line_id}>
                      <tr
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpanded((e) => ({ ...e, [ln.batch_line_id]: !e[ln.batch_line_id] }))}
                        data-testid={`batch-line-${ln.recipient_id}`}
                      >
                        <td className="px-2 py-2 text-slate-400">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{ln.recipient_name}</div>
                          <div className="text-xs text-slate-500 uppercase">{ln.recipient_type}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{(ln.actual_payment_method || ln.payment_method || '—').toString().toUpperCase()}</Badge>
                          {ln.payment_reference && (
                            <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{ln.payment_reference}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-emerald-700">{money(ln.positive_total)}</td>
                        <td className="px-3 py-2 text-right text-orange-600">
                          {ln.negative_offset_applied > 0 ? `-${money(ln.negative_offset_applied)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right text-violet-600">
                          {ln.manual_credit_applied > 0 ? `+${money(ln.manual_credit_applied)}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {money(ln.net_payable)}
                          {ln.line_type === 'zero_net' && (
                            <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">Zero-net</div>
                          )}
                          {ln.line_type === 'manual_credit' && (
                            <div className="text-[10px] uppercase tracking-wide text-violet-500 mt-0.5">Standalone credit</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`${lineMeta.tone} border font-normal`} data-testid={`line-status-${ln.recipient_id}`}>
                            {lineMeta.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-600">{ln.statement_count}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={9} className="px-6 py-3">
                            {/* Payment method editor for Ready-for-Payment batches */}
                            {readyForMethodEdit && isKR && ln.execution_status !== 'paid' && ln.execution_status !== 'failed' && (
                              <div className="mb-3">
                                <PaymentMethodEditor
                                  batchId={batchId}
                                  line={ln}
                                  onUpdated={handleMethodUpdated}
                                />
                              </div>
                            )}
                            {/* Failed action for KR — allowed only on lines already in `paid` status */}
                            {canFail && ln.execution_status === 'paid' && (
                              <div className="mb-3 flex justify-end">
                                <Button size="sm" variant="destructive" onClick={() => setFailLine(ln)} data-testid={`mark-failed-btn-${ln.recipient_id}`}>
                                  <XCircle className="h-4 w-4 mr-1.5" /> Mark Payment Failed
                                </Button>
                              </div>
                            )}
                            {ln.execution_status === 'failed' && (
                              <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-900">
                                <b>Payment Failed</b> — {(ln.failure_reason_code || '').replace(/_/g, ' ').toUpperCase()}
                                {ln.failure_notes && <span className="ml-2 italic">"{ln.failure_notes}"</span>}
                                {ln.reversed && <span className="ml-2 text-xs">· PAYMENT_REVERSAL posted</span>}
                              </div>
                            )}
                            <table className="w-full text-xs">
                              <thead className="text-slate-500">
                                <tr>
                                  <th className="text-left py-1 font-medium">Statement #</th>
                                  <th className="text-left py-1 font-medium">Period</th>
                                  <th className="text-left py-1 font-medium">Carrier</th>
                                  <th className="text-right py-1 font-medium">Gross</th>
                                  <th className="text-right py-1 font-medium">Offset</th>
                                  <th className="text-right py-1 font-medium">Included</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ln.statements.map((s) => (
                                  <tr key={s.statement_id} className="border-t border-slate-200">
                                    <td className="py-1 font-mono">
                                      <button
                                        className="text-blue-700 hover:underline"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/accounting/statements/${s.statement_id}`); }}
                                      >
                                        {s.statement_number}
                                      </button>
                                    </td>
                                    <td className="py-1">{s.period_label || '—'}</td>
                                    <td className="py-1">{s.carrier_name || '—'}</td>
                                    <td className="py-1 text-right">{money(s.gross_amount)}</td>
                                    <td className="py-1 text-right text-orange-600">{s.offset_amount > 0 ? `-${money(s.offset_amount)}` : '—'}</td>
                                    <td className="py-1 text-right font-medium">{money(s.included_amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Status History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Status History</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {(batch.status_history || []).map((h, i) => {
              const m = BATCH_STATUS_META[h.to_status] || { label: h.to_status, tone: 'bg-slate-100 text-slate-700 border-slate-300' };
              return (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Badge className={`${m.tone} border shrink-0 mt-0.5`}>{m.label}</Badge>
                  <div className="flex-1">
                    <div className="text-slate-700">
                      {h.by_name || '—'} · <span className="text-slate-500">{fmtDate(h.at)}</span>
                    </div>
                    {h.reason && <div className="text-xs text-slate-600 italic mt-0.5">"{h.reason}"</div>}
                  </div>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={(v) => !v && setRejectOpen(false)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Payment Batch</DialogTitle>
            <DialogDescription>
              Rejecting will release all statement reservations and remove any applied recovery offset entries. This is auditable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">Reason (required)</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Explain why this batch is being rejected…"
              data-testid="reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} disabled={actioning} data-testid="confirm-reject-btn">
              {actioning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reject Batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={(v) => !v && setCancelOpen(false)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Payment Batch</DialogTitle>
            <DialogDescription>
              Cancelling releases all reservations. The batch stays in history but cannot be restored.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>Keep Batch</Button>
            <Button variant="destructive" onClick={doCancel} disabled={actioning} data-testid="confirm-cancel-btn">
              Cancel Batch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Mark Failed dialog */}
      {failLine && (
        <MarkFailedDialog
          open={!!failLine}
          onClose={() => setFailLine(null)}
          batchId={batchId}
          line={failLine}
          onFailed={handleFailed}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, tone = 'slate', bold = false }) {
  const toneMap = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    orange:  'text-orange-700 bg-orange-50 border-orange-200',
    violet:  'text-violet-700 bg-violet-50 border-violet-200',
    slate:   'text-slate-800 bg-slate-50 border-slate-200',
  };
  return (
    <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500 mb-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={bold ? 'text-xl font-semibold' : 'text-lg font-medium'}>{value}</div>
    </div>
  );
}
